/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { NameCellLink } from 'src/core/components/NameCellLink';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getFormattedDate } from 'src/core/utils/dateFormatter';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { QnaMakerProjectStore } from 'src/qnaMaker/core/stores/QnaMakerProjectStore';

export type QnaMakerProjectsTableProps = TableProps<QnaMakerProjectStore> & {
    onProjectClick: (projectId: string) => void;
};

const messages = defineMessages({
    nameColumnHeader: {
        id: 'qnaMaker.qnaMakerProjectsTable.nameColumnHeader',
        defaultMessage: 'Name'
    },
    languageColumnHeader: {
        id: 'qnaMaker.qnaMakerProjectsTable.languageColumnHeader',
        defaultMessage: 'Language'
    },
    endpointHitsCountColumnHeader: {
        id: 'qnaMaker.qnaMakerProjectsTable.endpointHitsCountColumnHeader',
        defaultMessage: 'Endpoint hits'
    },
    modifiedDateColumnHeader: {
        id: 'qnaMaker.qnaMakerProjectsTable.modifiedDateColumnHeader',
        defaultMessage: 'Last modified'
    },
    publishedDateColumnHeader: {
        id: 'qnaMaker.qnaMakerProjectsTable.publishedDateColumnHeader',
        defaultMessage: 'Last published'
    }
});

export const QnaMakerProjectsTable = observer((props: QnaMakerProjectsTableProps) => {
    const { selection, items, onProjectClick: onAppClick, sortKey, ...rest } = props;
    const { localize } = useLocalization();

    const columns: IColumn[] = [
        {
            key: 'name',
            minWidth: 600,
            fieldName: 'name',
            isRowHeader: true,
            isResizable: true,
            name: localize(messages.nameColumnHeader),
            isSorted: sortKey && sortKey.key === 'name',
            isSortedDescending: sortKey && sortKey.key === 'name' && sortKey.order === 'desc',
            onRender: (item: QnaMakerProjectStore) => <NameCellLink onClick={() => onAppClick(item.id)}>{item.name}</NameCellLink>
        },
        {
            minWidth: 150,
            isResizable: true,
            key: 'modifiedDate',
            fieldName: 'modifiedDate',
            name: localize(messages.modifiedDateColumnHeader),
            isSorted: sortKey && sortKey.key === 'modifiedDate',
            isSortedDescending: sortKey && sortKey.key === 'modifiedDate' && sortKey.order === 'desc',
            onRender: (item: QnaMakerProjectStore) => (
                <LuisText variant="medium">{item.modifiedDate ? getFormattedDate(item.modifiedDate) : '--'}</LuisText>
            )
        },
        {
            minWidth: 150,
            key: 'language',
            isResizable: true,
            fieldName: 'language',
            name: localize(messages.languageColumnHeader),
            isSorted: sortKey && sortKey.key === 'language',
            isSortedDescending: sortKey && sortKey.key === 'language' && sortKey.order === 'desc',
            onRender: (item: QnaMakerProjectStore) => (
                <LuisText variant="medium">
                    {LuisConstants.QnaMaker.SupportedProjectLangues.find(l => l.code === item.language).name}
                </LuisText>
            )
        },
        {
            minWidth: 150,
            key: 'lastPublishedDate',
            isResizable: true,
            fieldName: 'lastPublishedDate',
            name: localize(messages.publishedDateColumnHeader),
            isSorted: sortKey && sortKey.key === 'lastPublishedDate',
            isSortedDescending: sortKey && sortKey.key === 'lastPublishedDate' && sortKey.order === 'desc',
            onRender: (item: QnaMakerProjectStore) => (
                <LuisText variant="medium">{item.lastPublishedDate ? getFormattedDate(item.lastPublishedDate) : '--'}</LuisText>
            )
        }
    ];

    return <Table<QnaMakerProjectStore> items={items} columns={columns} selection={selection} {...rest} />;
});
